import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import client from '../../../lib/feathers';
import EntitySearchForm from '../../EntitySearchForm';
import Pagination from '../../Pagination';
import PrimaryButton from '../../PrimaryButton';
import NewLibationForm from './NewLibationForm';
import Modal from '../../Modal';
import NewLibationVintageForm from './NewLibationVintageForm';
import LibationMergeModal from './LibationMergeModal';
import { setWineSearchKeywords, clearWineSearchKeywords } from '../../../lib/wineSearchSlice';
import { faWineBottle, faTag, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default function LibationsInfoPanel() {
  const { lid } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const wineSearchKeywords = useSelector((state) => state.wineSearch.keywords);

  const [libations, setLibations] = useState();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateVintage, setShowCreateVintage] = useState(false);
  const [libationVintage, setLibationVintage] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);

  // Pagination State:
  const LIMIT = 25;
  const [query] = useSearchParams();
  const [page, setPage] = useState(query.get('page') || 0);
  const [total, setTotal] = useState(0);
  const [keywords, setKeywords] = useState(wineSearchKeywords || query.keywords || '');

  const handleKeywordsChange = (newKeywords) => {
    setKeywords(newKeywords);
    if (!newKeywords) {
      dispatch(clearWineSearchKeywords());
    }
  };

  const loadLibations = async () => {
    const service = client.service(`${user.role}/libation`);

    const _query = {
      listingId: lid,
      $limit: LIMIT,
      $skip: page * LIMIT,
      $sort: {
        name: 1,
      },
    };

    if (keywords && keywords.length) {
      // Multi-token AND-of-iLike so "freedom pom" matches "Freedom Hill
      // Vineyard Pommard". Same pattern Discover / libation-search / the
      // listing pickers use.
      const tokens = keywords.trim().split(/\s+/).filter(Boolean);
      _query.$and = tokens.map((t) => ({ name: { $iLike: `%${t}%` } }));
    }

    const response = await service.find({ query: _query });
    setLibations(response.data);
    setTotal(response.total);
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(0);
    dispatch(setWineSearchKeywords(keywords));
    loadLibations();
  }; 

  const navigateToPage = (p = 0, kw) => {
    if (p * LIMIT > total || p * LIMIT < 0) return;
    let url = `/${user.role}/listings/${lid}/wines?page=${p}`;
    if (kw) {
      url += `&keywords=${kw}`;
    }
    navigate(url);
    setPage(p);
  };

  const openNewVintage = (libation) => {
    setLibationVintage(libation);
    setShowCreateVintage(true);
  }

  useEffect(() => {
    if (lid && user) {
      loadLibations();
    }
  }, [lid, user, page]);

  const AttachmentIndicators = ({ summary, size = 'sm' }) => {
    const s = summary || { bottleCount: 0, labelCount: 0, tradeSheetCount: 0 };
    const hasBottle = s.bottleCount > 0;
    const hasLabel = s.labelCount > 0;
    const hasTradeSheet = s.tradeSheetCount > 0;
    const iconSize = size === 'sm' ? 'text-xs' : 'text-sm';
    const bottleTip = hasBottle ? `${s.bottleCount} bottle photo${s.bottleCount === 1 ? '' : 's'}` : 'No bottle photo';
    const labelTip = hasLabel ? `${s.labelCount} label photo${s.labelCount === 1 ? '' : 's'}` : 'No label photo';
    const tradeTip = hasTradeSheet ? `${s.tradeSheetCount} tech / sales sheet${s.tradeSheetCount === 1 ? '' : 's'}` : 'No tech / sales sheet';
    return (
      <span className={`inline-flex items-center gap-1.5 ${iconSize}`}>
        <FontAwesomeIcon
          icon={faWineBottle}
          title={bottleTip}
          className={hasBottle ? 'text-primary' : 'text-gray-300'}
        />
        <FontAwesomeIcon
          icon={faTag}
          title={labelTip}
          className={hasLabel ? 'text-primary' : 'text-gray-300'}
        />
        <FontAwesomeIcon
          icon={faFileLines}
          title={tradeTip}
          className={hasTradeSheet ? 'text-primary' : 'text-gray-300'}
        />
      </span>
    );
  };

  const StatusChip = ({ status }) => {
    return (
      status === 'active' ?
        <span className={`inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20`}>Active</span>
      : status === 'inactive' ?
        <span className={`inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20`}>Inactive</span>
      : status === 'library' ?
        <span className={`inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20`}>Library</span>
      : status === 'member' ?
        <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">Member</span>
      : null
    )
  };

  const LibationVintages = ({ libation }) => {
    return <>
      {libation.libationVintages.map((lv, i) => (
        <div key={i} className="flex items-center gap-2">
          <a href={`/${user.role}/listings/${lid}/wines/${libation.id}/${lv.id}/edit`} className='underline'>{lv.vintage.year === 9999 ? 'NV' : lv.vintage.year}</a>
          <AttachmentIndicators summary={lv.attachmentSummary} />
        </div>
      ))}
      <button onClick={() => openNewVintage(libation)} className='underline'>Add Vintage</button>
    </>
  }

  return (
    <div className="bg-white shadow overflow-hidden px-5 py-5 sm:rounded-lg sm:p-6">
      {/* WINE LIST */}
      <div className="sm:items-center py-2">
        <div className="sm:flex sm:items-center py-2">
          <div className="sm:flex-auto">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Wine List</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Here's a list of all your wines, including user submitted wines that need review.
            </p>
          </div>
        </div>
        {/* SEARCH / PAGE BAR */}
        <div className="pb-5 border-gray-200 sm:flex sm:items-center sm:justify-between">
          <div className="flex items-center">
            <EntitySearchForm onSearch={onSearch} setKeywords={handleKeywordsChange} keywords={keywords} />
            <p className="text-sm font-medium leading-6 text-gray-900 ml-2">
              {`(${total}) Total Wines`}
            </p>
          </div>
          <div className="mt-3 sm:mt-0 sm:ml-4 flex gap-2">
            {user.role === 'admin' && (
              <button
                className="items-center px-4 py-2 my-1 border rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                onClick={() => setShowMergeModal(true)}
                title="Review and merge dupe libations on this listing"
              >
                Review for Dupes
              </button>
            )}
            <button className="items-center px-4 py-2 my-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-icon hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" onClick={() => setShowCreateModal(true)}>Create New Wine</button>
          </div>
        </div>
        <div>
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="hidden py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3 lg:table-cell">
                      Name
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                      Vintages
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                      Varietal
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                      Type
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 xl:table-cell">
                      Date Created
                    </th>
                    <th scope="col" className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 xl:table-cell">
                      Status
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                      <span className="sr-only">Manage</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {libations?.map((libation, libationIndex) => (
                    <tr key={libationIndex} className={libationIndex % 2 === 0 ? undefined : 'bg-gray-50'}>
                      <td>
                        <dl className="whitespace-nowrap py-1 pl-3 pr-3 text-sm font-medium text-gray-900 flex relative items-center">
                          <span className="flex items-center text-sm font-medium relative">
                            <div className="group">
                              {libation.needsReview && (
                                <>
                                  <span className="mr-1 flex w-2.5 h-2.5 bg-yellow-500 rounded-full me-1.5 flex-shrink-0"></span>
                                  <span className="hidden absolute z-10 -top-6 rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20 group-hover:block">
                                    This wine needs to be reviewed
                                  </span>
                                </>
                              )
                              }
                            </div>
                            <Link to={`/${user.role}/listings/${lid}/wines/${libation.id}`} className="text-base lg:text-sm text-primary hover:text-red-900 hover:underline">{libation.name}</Link>
                            <span className="ml-2"><AttachmentIndicators summary={libation.attachmentSummary} /></span>
                          </span>
                        </dl>
                        {libation.libationVintages && (
                          <dd className="whitespace-nowrap px-3 py-1 text-sm text-gray-500 lg:hidden">
                            <LibationVintages libation={libation} />
                          </dd>
                        )}
                        {libation.varietal && (
                          <dd className="whitespace-nowrap px-3 py-1 text-sm text-gray-500 lg:hidden">
                            <span><span className="text-black font-medium">Varietal : </span>{libation.varietal.name}</span>
                          </dd>
                        )}
                        {libation.libationType && (
                          <dd className="whitespace-nowrap px-3 py-1 text-sm text-gray-500 lg:hidden">
                            <span>({libation.libationType.name})</span>
                          </dd>
                        )}
                        <dd className="whitespace-nowrap px-3 py-1 text-sm font-medium xl:hidden"> Created : <span className="text-gray-500 font-normal">{new Date(libation.createdAt).toDateString()}</span></dd>
                        <dd className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 xl:hidden" >
                          <StatusChip status={libation.status} />
                        </dd>
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                        <LibationVintages libation={libation} />
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                        {libation.varietal?.name}
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                        {libation.libationType?.name}
                      </td>
                      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 xl:table-cell">{new Date(libation.createdAt).toDateString()}</td>
                      {/* <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">"Status"</td> */}
                      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 xl:table-cell">
                        <StatusChip status={libation.status} />
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <div className="flex-row lg:flex lg:justify-between">
                          <button className="items-center px-4 py-2 my-1 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-icon hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" onClick={() => navigate(`/${user.role}/listings/${lid}/wines/${libation.id}/edit`)}>Edit</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div >
      </div >

      <div className="mt-3 sm:mt-0 sm:ml-4">
        <Pagination total={total} limit={LIMIT} page={page} navigateToPage={navigateToPage} />
      </div>

      {showCreateModal
        && (
          <Modal>
            <NewLibationForm onCancelClick={() => setShowCreateModal(false)} />
          </Modal>
        )}
      {showCreateVintage
        && (
          <Modal>
            <NewLibationVintageForm libation={libationVintage} onCancelClick={() => setShowCreateVintage(false)} />
          </Modal>
        )}
      <LibationMergeModal
        open={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        onComplete={() => loadLibations()}
        libations={libations || []}
      />
    </div >
  );
}